import {
  DevicePresenceStateDto,
  IotDeviceStateOfUse,
  QaEquipmentCatalogDto,
  QaEquipmentRtSystemDto,
  QaEquipmentStateOfUse,
} from '@/api/generator';
import { connectedArrayInfoOf } from '@/components/deviceStatus/utils/deviceInformation';

/**
 * One array the user can pick as the one connected to the interface: an
 * enabled array of the same model as the set's array, identified by the
 * serial number printed on its label.
 */
export interface DetectorCandidate {
  id: string;
  name: string;
  model: string;
  serialNumber: string;
}

/**
 * The candidates among the fleet for the set's array: enabled arrays of the
 * same model that carry a serial number, sorted by serial number. Model
 * only — sub-models of one model share the type label the user reads. The
 * set's own array is a candidate like any other, so the list gives no hint
 * which one is right. Without a serial number an array cannot be told apart
 * on its label, so it is left out.
 */
export function detectorCandidatesOf(
  fleet: QaEquipmentRtSystemDto[],
  array: QaEquipmentCatalogDto,
): DetectorCandidate[] {
  return fleet
    .filter(
      (device) =>
        device.subCategory === array.subCategory &&
        device.model === array.model &&
        device.state === QaEquipmentStateOfUse.Enabled &&
        'serialNumber' in device &&
        !!device.serialNumber,
    )
    .map((device) => ({
      id: device.id,
      name: device.name,
      model: device.model,
      serialNumber: (device as { serialNumber: string }).serialNumber,
    }))
    .sort((a, b) => a.serialNumber.localeCompare(b.serialNumber, undefined, { numeric: true }));
}

/**
 * What the detector interface currently reports about its array, for the
 * informational line in the confirmation box. The interface only names the
 * array's type, never its serial number, and the type is a firmware
 * designation that is not mapped onto catalog models — so this is shown,
 * not compared.
 */
export type ReportedArray =
  | { state: 'offline' }
  | { state: 'noArray' }
  | { state: 'connected'; typeName: string };

export function reportedArrayOf(presence: DevicePresenceStateDto | undefined): ReportedArray {
  const online =
    presence?.state === IotDeviceStateOfUse.Online ||
    presence?.state === IotDeviceStateOfUse.Initializing ||
    presence?.state === IotDeviceStateOfUse.Measuring;
  if (!online) {
    return { state: 'offline' };
  }
  const array = connectedArrayInfoOf(presence.deviceInformation);
  return array ? { state: 'connected', typeName: array.name } : { state: 'noArray' };
}

/** Key of a confirmation: the physical array at the linac of this job occurrence. */
export function detectorConfirmationKey(
  jobOccurrenceId: string | undefined,
  arrayId: string | undefined,
): string | undefined {
  return jobOccurrenceId && arrayId ? `${jobOccurrenceId}:${arrayId}` : undefined;
}
